"use client";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";

export const BackgroundGradientAnimation = ({
  gradientBackgroundStart = "rgb(108, 0, 162)",
  gradientBackgroundEnd = "rgb(0, 17, 82)",
  firstColor = "18, 113, 255",
  secondColor = "221, 74, 255",
  thirdColor = "100, 220, 255",
  fourthColor = "200, 50, 50",
  fifthColor = "180, 180, 50",
  pointerColor = "140, 100, 255",
  size = "80%",
  blendingValue = "hard-light",
  children,
  className,
  interactive = false, // Disabled by default for performance
  containerClassName,
}: {
  gradientBackgroundStart?: string;
  gradientBackgroundEnd?: string;
  firstColor?: string;
  secondColor?: string;
  thirdColor?: string;
  fourthColor?: string;
  fifthColor?: string;
  pointerColor?: string;
  size?: string;
  blendingValue?: string;
  children?: React.ReactNode;
  className?: string;
  interactive?: boolean;
  containerClassName?: string;
}) => {
  const interactiveRef = useRef<HTMLDivElement>(null);
  const [curX, setCurX] = useState(0);
  const [curY, setCurY] = useState(0);
  const [tgX, setTgX] = useState(0);
  const [tgY, setTgY] = useState(0);
  const [isSafari, setIsSafari] = useState(false);
  const [isLowPerfDevice, setIsLowPerfDevice] = useState(false);

  // Memoize CSS variables to prevent layout shifts
  const cssVariables = useMemo(() => ({
    '--gradient-background-start': gradientBackgroundStart,
    '--gradient-background-end': gradientBackgroundEnd,
    '--first-color': firstColor,
    '--second-color': secondColor,
    '--third-color': thirdColor,
    '--fourth-color': fourthColor,
    '--fifth-color': fifthColor,
    '--pointer-color': pointerColor,
    '--size': size,
    '--blending-value': blendingValue,
  } as React.CSSProperties), [gradientBackgroundStart, gradientBackgroundEnd, firstColor, secondColor, thirdColor, fourthColor, fifthColor, pointerColor, size, blendingValue]);

  useEffect(() => {
    // Detect low-performance devices
    const isLowPerf = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      navigator.hardwareConcurrency <= 4 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsLowPerfDevice(isLowPerf);
    setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
  }, []);

  // Throttled mouse move handler for performance
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || !interactiveRef.current) return;
    const rect = interactiveRef.current.getBoundingClientRect();
    setTgX(event.clientX - rect.left);
    setTgY(event.clientY - rect.top);
  }, [interactive]);

  // Only run animation on desktop with good performance
  useEffect(() => {
    if (!interactive || isLowPerfDevice) return;
    
    let animationId: number;
    function move() {
      if (!interactiveRef.current) return;
      setCurX(prev => prev + (tgX - prev) / 20);
      setCurY(prev => prev + (tgY - prev) / 20);
      interactiveRef.current.style.transform = `translate(${Math.round(curX)}px, ${Math.round(curY)}px)`;
      animationId = requestAnimationFrame(move);
    }
    animationId = requestAnimationFrame(move);
    return () => cancelAnimationFrame(animationId);
  }, [tgX, tgY, interactive, isLowPerfDevice, curX, curY]);

  return (
    <div
      className={cn(
        "h-screen w-screen relative overflow-hidden top-0 left-0",
        containerClassName
      )}
      style={{
        ...cssVariables,
        background: `linear-gradient(40deg, ${gradientBackgroundStart}, ${gradientBackgroundEnd})`,
        willChange: 'auto', // Prevent unnecessary layer creation
        contain: 'layout style paint', // CSS containment for performance
      }}
    >
      <svg className="hidden">
        <defs>
          <filter id="blurMe">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="10"
              result="blur"
            />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>
      <div className={cn("", className)}>{children}</div>
      
      {/* Simplified gradients for low-perf devices */}
      {isLowPerfDevice ? (
        <div className="gradients-container h-full w-full blur-lg opacity-50">
          <div
            className="absolute w-[80%] h-[80%] top-[10%] left-[10%] rounded-full"
            style={{
              background: `radial-gradient(circle at center, rgba(${firstColor}, 0.3) 0%, transparent 70%)`,
              mixBlendMode: blendingValue as any,
            }}
          />
        </div>
      ) : (
        <div
          className={cn(
            "gradients-container h-full w-full blur-lg",
            isSafari ? "blur-2xl" : "[filter:url(#blurMe)_blur(40px)]"
          )}
        >
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(${firstColor},1)_0,_rgba(${firstColor},0)_50%)_no-repeat]`,
              `w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:center_center]`,
              `animate-first`,
              `opacity-100`
            )}
            style={{ mixBlendMode: blendingValue as any, '--size': size } as React.CSSProperties}
          />
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(${secondColor},_0.8)_0,_rgba(${secondColor},_0)_50%)_no-repeat]`,
              `w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:calc(50%-400px)]`,
              `animate-second`,
              `opacity-100`
            )}
            style={{ mixBlendMode: blendingValue as any, '--size': size } as React.CSSProperties}
          />
          <div
            className={cn(
              `absolute [background:radial-gradient(circle_at_center,_rgba(${thirdColor},_0.8)_0,_rgba(${thirdColor},_0)_50%)_no-repeat]`,
              `w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]`,
              `[transform-origin:calc(50%+400px)]`,
              `animate-third`,
              `opacity-100`
            )}
            style={{ mixBlendMode: blendingValue as any, '--size': size } as React.CSSProperties}
          />

          {interactive && !isLowPerfDevice && (
            <div
              ref={interactiveRef}
              onMouseMove={handleMouseMove}
              className={cn(
                `absolute [background:radial-gradient(circle_at_center,_rgba(${pointerColor},_0.8)_0,_rgba(${pointerColor},_0)_50%)_no-repeat]`,
                `w-full h-full -top-1/2 -left-1/2`,
                `opacity-70`
              )}
              style={{ mixBlendMode: blendingValue as any }}
            />
          )}
        </div>
      )}
    </div>
  );
};